/**
 * Client-side best line calculation utilities
 * Allows recalculating best lines based on user preferences (excluded sportsbooks)
 */

import type { BookLine, BestLine, BestLines, MarketCode, PlayerWithMarkets } from '@sharpr/types';
import { eligibleForBestLine, type BestLineEligibility } from '@sharpr/pricing';

/**
 * Best EV-bearing line across over+under sides of a single market's
 * `bestLines`. Removed lines and missing `ev` are skipped. Returns `undefined`
 * if neither side qualifies.
 *
 * Single source of truth for "what's the best line for this market" — drives
 * the card badge / odds-table filter / sort and the player-level `bestMarket`.
 */
function getBestLineForMarket(bestLines: BestLines | undefined): BestLine | undefined {
  if (!bestLines) return undefined;
  const { over, under } = bestLines;
  const overEligible = over?.ev != null && !over.isRemoved;
  const underEligible = under?.ev != null && !under.isRemoved;
  if (overEligible && underEligible) return over!.ev! >= under!.ev! ? over : under;
  if (overEligible) return over;
  if (underEligible) return under;
  return undefined;
}

/** Best EV across over+under sides of a single market. `-Infinity` if neither side qualifies. */
export function getBestEvForMarket(bestLines: BestLines | undefined): number {
  return getBestLineForMarket(bestLines)?.ev ?? -Infinity;
}

/**
 * Best EV across all of a player's markets. Pass `marketPredicate` to restrict
 * to a subset (e.g. the user's active market filter). Returns `-Infinity` when
 * no eligible market line exists.
 */
export function getBestEvForPlayer(
  markets: PlayerWithMarkets['markets'],
  marketPredicate?: (market: MarketCode) => boolean,
): number {
  let best = -Infinity;
  for (const [market, data] of Object.entries(markets)) {
    if (!data) continue;
    if (marketPredicate && !marketPredicate(market as MarketCode)) continue;
    const ev = getBestEvForMarket(data.bestLines);
    if (ev > best) best = ev;
  }
  return best;
}

/** Best over/under lines from book lines, among the sides `eligibility` allows. */
export function calculateBestLines(books: BookLine[], eligibility: BestLineEligibility): BestLines {
  const filteredBooks = books.filter(b => !b.isRemoved && eligibleForBestLine(eligibility, b.book, b.betLimit));

  function bestForSide(side: 'over' | 'under'): BestLine | undefined {
    const sideBooks = filteredBooks.filter(b => b.side === side && b.ev != null);
    if (sideBooks.length > 0) {
      return sideBooks.reduce<BookLine>((best, current) =>
        (current.ev ?? 0) > (best.ev ?? 0) ? current : best
      , sideBooks[0]);
    }
    // No EV data — fallback: best odds at most favorable line
    const allSideBooks = filteredBooks.filter(b => b.side === side);
    if (allSideBooks.length === 0) return undefined;
    return allSideBooks.reduce<BookLine>((best, current) => {
      const betterLine = side === 'over'
        ? current.line < best.line
        : current.line > best.line;
      if (betterLine || (current.line === best.line && current.odds > best.odds)) return current;
      return best;
    }, allSideBooks[0]);
  }

  return {
    over: bestForSide('over'),
    under: bestForSide('under'),
  };
}

/**
 * Compute bestMarket from a markets record. Picks the market with the highest
 * positive EV; falls back to the first market with any visible best line so
 * odds-only entries still render somewhere.
 *
 * Accepts `PlayerWithMarkets['markets']` (partial keyed by `MarketCode`) and
 * any structurally-compatible record. Used by both the client (recompute on
 * user-pref change) and the server (admin-strip pipeline).
 */
type MarketsForBestMarket = {
  [k: string]: { bestLines: BestLines } | undefined;
};

export function computeBestMarket(
  markets: MarketsForBestMarket,
): { market: MarketCode; ev: number; line: BestLine } | undefined {
  let bestMarketCode: string | undefined;
  let bestMarketEV = -Infinity;
  let bestMarketLine: BestLine | undefined;

  for (const [marketCode, marketData] of Object.entries(markets)) {
    if (!marketData) continue;
    const line = getBestLineForMarket(marketData.bestLines);
    const ev = line?.ev ?? -Infinity;
    if (ev > 0 && ev > bestMarketEV) {
      bestMarketEV = ev;
      bestMarketCode = marketCode;
      bestMarketLine = line;
    }
  }

  // Fallback: if no +EV market found, pick first market with a visible best line
  if (!bestMarketCode) {
    for (const [marketCode, marketData] of Object.entries(markets)) {
      if (!marketData) continue;
      const line = marketData.bestLines.over ?? marketData.bestLines.under;
      if (line && !line.isRemoved) {
        bestMarketCode = marketCode;
        bestMarketEV = 0;
        bestMarketLine = line;
        break;
      }
    }
  }

  return bestMarketCode && bestMarketLine
    ? { market: bestMarketCode as MarketCode, ev: bestMarketEV, line: bestMarketLine }
    : undefined;
}

function recalcMarkets<T extends Record<string, { books: BookLine[]; bestLines: BestLines; projection: number | null }>>(
  markets: T,
  eligibility: BestLineEligibility
): T {
  const updated = {} as T;
  for (const [marketCode, marketData] of Object.entries(markets)) {
    (updated as Record<string, unknown>)[marketCode] = {
      ...marketData,
      bestLines: calculateBestLines(marketData.books, eligibility)
    };
  }
  return updated;
}

/**
 * Recalculate a player's best lines (and bestMarket, so sorting reflects user
 * preferences) with excluded sportsbooks removed.
 */
export function recalculatePlayerBestLines<T extends {
  markets: Record<string, { books: BookLine[]; bestLines: BestLines; projection: number | null }>;
  bestMarket?: { market: MarketCode; ev: number; line: BestLine };
}>(
  player: T,
  eligibility: BestLineEligibility
): T {
  const updatedMarkets = recalcMarkets(player.markets, eligibility);

  return {
    ...player,
    markets: updatedMarkets,
    bestMarket: computeBestMarket(updatedMarkets),
  };
}
